import { popupTargetFromElement } from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  CenterPeekIcon,
  MoreHorizontalIcon,
  PlusIcon,
} from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { autoPlacement, offset, shift } from '@floating-ui/dom';
import { signal } from '@preact/signals-core';
import { cssVarV2 } from '@streamlain/theme/v2';
import { css, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { html } from 'lit/static-html.js';

import { popPropertiesSetting } from '../../../core/common/properties.js';
import type { KanbanColumn } from '../kanban-view-manager.js';
import type { KanbanViewUILogic } from './kanban-view-ui-logic.js';
import { openDetail, popCardMenu } from './menu.js';

const styles = css`
  affine-data-view-kanban-card {
    display: flex;
    position: relative;
    flex-direction: column;
    border: 1px solid ${unsafeCSS(cssVarV2.layer.insideBorder.border)};
    box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    transition: background-color 100ms ease-in-out;
    /* Card background reads the V2 layer token, which IS defined in
       every theme preset (blue-white preset sets
       --streamlain-v2-layer-background-primary on :root). The old
       --streamlain-background-kanban-card-color was only scoped to
       .data-view-drag-preview, so outside that scope the card
       background resolved to transparent and the group-tint bled
       through. Kept on its own line so a future theme wanting a
       dedicated kanban-card surface color can add the
       data-view-drag-preview override back as a first-param here. */
    background-color: ${unsafeCSS(cssVarV2.layer.background.primary)};
  }

  affine-data-view-kanban-card:hover {
    background-color: ${unsafeCSS(cssVarV2.layer.background.primary)};
    background-image: linear-gradient(
      ${unsafeCSS(cssVarV2.layer.background.hoverOverlay)},
      ${unsafeCSS(cssVarV2.layer.background.hoverOverlay)}
    );
  }

  /* Selected card ring + wash. Cursor on descendants is NOT forced to
     grab any more — the drag affordance lives on the dedicated
     .card-drag-handle, so cells keep their native text cursor and
     property icons keep their pointer. Forcing grab everywhere used
     to mislead users into thinking the whole card was a drag surface
     and suppressed the text cursor on editable cells. */
  affine-data-view-kanban-card[data-selected] {
    background-color: var(--streamlain-primary-color-04);
    box-shadow:
      0 0 0 2px var(--streamlain-primary-color),
      0 2px 4px 0 rgba(0, 0, 0, 0.08);
  }
  affine-data-view-kanban-card[data-selected]:hover {
    background-color: var(--streamlain-primary-color-04);
    background-image: none;
  }
  /* Shift held — card-level range-select hint. Wins over grab. */
  [data-shift-active] affine-data-view-kanban-card,
  [data-shift-active] affine-data-view-kanban-card * {
    cursor: cell;
  }

  affine-data-view-kanban-card .card-header {
    padding: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  affine-data-view-kanban-card .card-header-title uni-lit {
    width: 100%;
  }

  .card-header.has-divider {
    border-bottom: 0.5px solid ${unsafeCSS(cssVarV2.layer.insideBorder.border)};
  }

  affine-data-view-kanban-card .card-header-title {
    flex: 1;
    min-width: 0;
    font-size: var(--data-view-cell-text-size);
    line-height: var(--data-view-cell-text-line-height);
  }

  affine-data-view-kanban-card .card-header-icon {
    padding: 4px;
    background-color: var(--streamlain-background-secondary-color);
    display: flex;
    align-items: center;
    border-radius: 4px;
    width: max-content;
  }

  affine-data-view-kanban-card .card-header-icon svg {
    width: 16px;
    height: 16px;
    fill: var(--streamlain-icon-color);
    color: var(--streamlain-icon-color);
  }

  affine-data-view-kanban-card .card-body {
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 4px;
  }

  affine-data-view-kanban-card:hover .card-ops {
    visibility: visible;
  }
  affine-data-view-kanban-card:has(.active) .card-ops {
    visibility: visible;
  }

  affine-data-view-kanban-card:has([data-editing='true']) .card-ops {
    visibility: hidden;
  }

  .card-ops {
    position: absolute;
    right: 8px;
    top: 8px;
    visibility: hidden;
    display: flex;
    gap: 4px;
    cursor: pointer;
  }

  .card-op {
    display: flex;
    position: relative;
    padding: 4px;
    border-radius: 4px;
    box-shadow: 0px 0px 4px 0px rgba(66, 65, 73, 0.14);
    background-color: var(--streamlain-background-primary-color);
  }

  .card-op:hover:before {
    content: '';
    border-radius: 4px;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: var(--streamlain-hover-color);
    pointer-events: none;
  }

  .card-op svg {
    position: relative;
    z-index: 1;
    fill: var(--streamlain-icon-color);
    color: var(--streamlain-icon-color);
    width: 16px;
    height: 16px;
  }

  .card-property-add {
    position: absolute;
    left: 50%;
    bottom: 6px;
    transform: translateX(-50%) translateY(4px);
    width: 28px;
    height: 24px;
    border: 1px solid var(--streamlain-border-color);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${unsafeCSS(cssVarV2.layer.background.primary)};
    color: var(--streamlain-icon-color);
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.12);
    opacity: 0;
    visibility: hidden;
    cursor: pointer;
    transition:
      opacity 120ms ease,
      transform 120ms ease,
      background-color 120ms ease;
  }

  affine-data-view-kanban-card:hover .card-property-add,
  .card-property-add:focus-visible {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

  .card-property-add:hover {
    background-color: ${unsafeCSS(cssVarV2.layer.background.primary)};
    background-image: linear-gradient(
      ${unsafeCSS(cssVarV2.layer.background.hoverOverlay)},
      ${unsafeCSS(cssVarV2.layer.background.hoverOverlay)}
    );
  }

  .card-property-add svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
    color: currentColor;
  }

  /* Notch-style grab handle. iOS home-indicator pill centered on a
     narrow strip at the top of the card. The strip itself is the
     pointer target — wide enough to forgive imprecise grabs — but
     only the pill is painted, so the chrome stays minimal. Always
     visible, amplifies on hover (pill widens + darkens + background
     tints). Rounds the top card corners so the strip blends into the
     card silhouette instead of sitting as a separate bar. */
  .card-drag-handle {
    position: relative;
    height: 14px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    flex-shrink: 0;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background-color: transparent;
    transition: background-color 160ms ease;
  }
  .card-drag-handle::before {
    content: '';
    display: block;
    width: 32px;
    height: 4px;
    border-radius: 2px;
    background-color: var(--streamlain-text-secondary-color);
    opacity: 0.35;
    transition:
      width 180ms cubic-bezier(0.2, 0, 0, 1),
      opacity 160ms ease,
      background-color 160ms ease;
  }
  affine-data-view-kanban-card:hover .card-drag-handle::before {
    opacity: 0.55;
    width: 38px;
  }
  .card-drag-handle:hover {
    background-color: var(--streamlain-hover-color);
  }
  .card-drag-handle:hover::before {
    width: 44px;
    opacity: 0.9;
    background-color: var(--streamlain-text-primary-color);
  }
  .card-drag-handle:active {
    cursor: grabbing;
  }
  .card-drag-handle:active::before {
    width: 48px;
    opacity: 1;
  }
`;

export class KanbanCard extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = styles;

  private readonly clickEdit = (e: MouseEvent) => {
    e.stopPropagation();
    const selection = this.getSelection();
    if (selection) {
      openDetail(this.kanbanViewLogic, this.cardId, selection);
    }
  };

  private readonly clickMore = (e: MouseEvent) => {
    e.stopPropagation();
    const selection = this.getSelection();
    const ele = e.currentTarget as HTMLElement;
    if (selection) {
      selection.selection = {
        selectionType: 'card',
        cards: [
          {
            groupKey: this.groupKey,
            rowKey: this.rowKey,
            cardId: this.cardId,
          },
        ],
      };
      popCardMenu(
        this.kanbanViewLogic,
        popupTargetFromElement(ele),
        this.cardId,
        selection
      );
    }
  };

  private readonly clickProperties = (e: MouseEvent) => {
    e.stopPropagation();
    popPropertiesSetting(
      popupTargetFromElement(e.currentTarget as HTMLElement),
      { view: this.view },
      [
        autoPlacement({ allowedPlacements: ['left', 'right'] }),
        offset(8),
        shift({ crossAxis: true }),
      ]
    );
  };

  private readonly contextMenu = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const selection = this.getSelection();
    if (selection) {
      selection.selection = {
        selectionType: 'card',
        cards: [
          {
            groupKey: this.groupKey,
            rowKey: this.rowKey,
            cardId: this.cardId,
          },
        ],
      };
      const target = e.target as HTMLElement;
      const ref = target.closest('affine-data-view-kanban-cell') ?? this;
      popCardMenu(
        this.kanbanViewLogic,
        popupTargetFromElement(ref),
        this.cardId,
        selection
      );
    }
  };

  private getSelection() {
    return this.kanbanViewLogic.selectionController;
  }

  private renderBody(columns: KanbanColumn[]) {
    if (columns.length === 0) {
      return '';
    }
    return html` <div class="card-body">
      ${repeat(
        columns,
        v => v.id,
        column => {
          if (this.view.isInHeader(column.id)) {
            return '';
          }
          return html` <affine-data-view-kanban-cell
            .contentOnly="${false}"
            data-column-id="${column.id}"
            .groupKey="${this.groupKey}"
            .rowKey="${this.rowKey}"
            .column="${column}"
            .cardId="${this.cardId}"
            .kanbanViewLogic="${this.kanbanViewLogic}"
          ></affine-data-view-kanban-cell>`;
        }
      )}
    </div>`;
  }

  private renderHeader(columns: KanbanColumn[]) {
    if (!this.view.hasHeader(this.cardId)) {
      return '';
    }
    const classList = classMap({
      'card-header': true,
      'has-divider': columns.length > 0,
    });
    return html`
      <div class="${classList}">${this.renderIcon()} ${this.renderTitle()}</div>
    `;
  }

  private renderIcon() {
    const icon = this.view.getHeaderIcon(this.cardId);
    if (!icon) {
      return;
    }
    return html` <div class="card-header-icon">
      ${icon.cellGetOrCreate(this.cardId).value$.value}
    </div>`;
  }

  private renderOps() {
    if (this.view.readonly$.value) {
      return;
    }
    return html`
      <div class="card-ops">
        <div class="card-op" @click="${this.clickEdit}">
          ${CenterPeekIcon()}
        </div>
        <div class="card-op" @click="${this.clickMore}">
          ${MoreHorizontalIcon()}
        </div>
      </div>
    `;
  }

  private renderPropertyAdd() {
    if (this.view.readonly$.value) {
      return;
    }
    return html`<button
      class="card-property-add"
      aria-label="Show card properties"
      title="Show card properties"
      @click="${this.clickProperties}"
    >
      ${PlusIcon()}
    </button>`;
  }

  private renderTitle() {
    const title = this.view.getHeaderTitle(this.cardId);
    if (!title) {
      return;
    }
    return html` <div class="card-header-title">
      <affine-data-view-kanban-cell
        .contentOnly="${true}"
        data-column-id="${title.id}"
        .kanbanViewLogic="${this.kanbanViewLogic}"
        .groupKey="${this.groupKey}"
        .rowKey="${this.rowKey}"
        .column="${title}"
        .cardId="${this.cardId}"
      ></affine-data-view-kanban-cell>
    </div>`;
  }

  override connectedCallback() {
    super.connectedCallback();
    if (this.view.readonly$.value) {
      return;
    }
    this._disposables.addFromEvent(this, 'contextmenu', e => {
      this.contextMenu(e);
    });
    // Click runs in the capture phase so the shared RowSelectionController
    // owns Shift/Ctrl/Meta semantics before any cell handler can steal
    // the gesture. A plain click on an unselected card collapses the
    // selection to this single card; a plain click on an already-
    // selected card preserves the set (so the user can follow up with
    // a drag without losing their multi-select).
    this._disposables.addFromEvent(this, 'click', this._handleCardClick, true);

    // Pointerdown installs a 4px deadzone and hands off to the shared
    // live-preview reorder when the user actually drags. Single-clicks
    // never reach here (deadzone); modifier clicks are routed through
    // `_handleCardClick` above.
    this._disposables.addFromEvent(
      this,
      'pointerdown',
      this._handleCardPointerDown
    );
  }

  private readonly _handleCardClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    // The "open detail" icon and the "..." menu have their own handlers
    // — let them through so click on those icons doesn't collapse the
    // row selection.
    if (target?.closest('.card-op, .card-property-add')) return;

    // A click event that follows a drag gesture (pointermove crossed
    // the 4 px threshold between down and up) is NOT a plain click —
    // treat it as a drag artifact and swallow it so neither the
    // selection clear below nor a cell focus kicks in on what the
    // user experienced as a drop.
    if (this._dragOccurred) {
      this._dragOccurred = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const selection = this.kanbanViewLogic.rowSelection;
    const inSel = selection.selectedIds$.value.has(this.cardId);

    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      selection.select(
        this.cardId,
        { shift: e.shiftKey, ctrl: e.ctrlKey, meta: e.metaKey },
        this.groupKey
      );
      return;
    }

    if (inSel) {
      // Card already in the selection. In a multi-row set we must NOT
      // let the click bubble to the cell handler — it would flip the
      // view's selection to `{type:cell}` and the sync effect would
      // then wipe the whole multi-row set. With a single-row set we
      // DO let the click through: that's the user's way to enter cell
      // focus/edit mode on the currently-selected card.
      if (selection.selectedIds$.value.size > 1) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    // Plain click on a NOT-selected card: collapse the selection to
    // this card. If the user clicked a concrete cell, let the click
    // continue into the cell handler; after the rowSelection write
    // below, that handler can enter edit mode directly instead of
    // requiring a second click. Non-cell card surface still stops here
    // and behaves as card selection.
    selection.select(this.cardId, {}, this.groupKey);
    if (!target?.closest('affine-data-view-kanban-cell')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  /**
   * Flag set by the pointerdown handler the moment a drag crosses
   * the 4 px threshold, so the `click` event that browsers still
   * synthesise after a mouse-up-with-drag can be told apart from a
   * genuine plain click. Read-once semantics — the click handler
   * resets it so a later plain-click on the same card still behaves
   * as "clear selection".
   */
  private _dragOccurred = false;

  private readonly _handleCardPointerDown = (e: PointerEvent): void => {
    if (this.view.readonly$.value) return;
    if (e.button !== 0 || !e.isPrimary) return;
    if (e.shiftKey || e.ctrlKey || e.metaKey) return;

    const target = e.target as HTMLElement | null;
    if (!target) return;
    // The whole card surface acts as a drag handle — a dedicated grip
    // lives at the top-center for discoverability, but every non-
    // interactive pixel should also initiate a drag on pointerdown
    // plus deadzone. Only targets where the user can actually input
    // or act are exempted so clicks there aren't swallowed.
    // • `.card-op`: the "open detail" / "..." action icons
    // • `input, textarea, [contenteditable="true"]`: any active
    //   editor (rich-text cell mid-edit, text-field cell, etc.)
    // • `a[href], button, [role="button"]`: native interactive
    //   elements inside property cells (eg. member-cell link chips)
    // • `.dv-cell-interactive`: opt-in escape hatch for cell
    //   renderers that need full pointer control
    // The drag handle itself carries `role="button"` for a11y, so it
    // would otherwise match the interactive exempt below. Short-
    // circuit when the target is under the handle so it always arms.
    const isOnHandle = !!target.closest('.card-drag-handle');
    if (!isOnHandle) {
      if (target.closest('.card-op, .card-property-add')) return;
      if (target.closest('input, textarea, [contenteditable="true"]')) return;
      if (target.closest('a[href], button, [role="button"], select')) return;
      if (target.closest('.dv-cell-interactive')) return;
    }

    // Selection promotion always runs so the follow-up click handler
    // sees `inSel=true` and lets the click through to the cell's edit
    // trigger. Without this, a first click on an unselected card's
    // cell sets `inSel=false` in `_handleCardClick`, which calls
    // `stopPropagation()` and blocks the cell's click listener.
    const sel = this.kanbanViewLogic.rowSelection;
    const wasAlreadySelected = sel.selectedIds$.value.has(this.cardId);
    if (!wasAlreadySelected) {
      sel.select(this.cardId, {}, this.groupKey);
    }

    e.stopPropagation();
    // NO `setPointerCapture` on the card here. Capturing the pointer
    // on the card reroutes all subsequent compatibility mouse events
    // (click, dblclick) onto the card element — which means cell-
    // level click + dblclick listeners never fire, breaking inline
    // editing. Window-level `pointermove/up` listeners below already
    // catch the deadzone gesture without needing capture; and once
    // the user crosses the 4 px threshold `startCardReorder` installs
    // its own capture on the reorder host, so drag still tracks off-
    // element pointers correctly.

    const startX = e.clientX;
    const startY = e.clientY;
    const pointerId = e.pointerId;
    let armed = true;

    // Larger deadzone from non-handle pointerdowns (cells, card body):
    // the whole card is a drag surface, but cells still need to be
    // click-to-edit — trackpad micro-movements easily cross 4 px. The
    // top notch uses its own `cursor: grab` so grip grabs are
    // unambiguous, a cell click isn't. Handle pointerdowns drop to 4
    // px since the user's intent there is clearly "drag".
    const DEADZONE = isOnHandle ? 4 : 10;
    const onMove = (move: PointerEvent): void => {
      if (!armed) return;
      if (move.pointerId !== pointerId) return;
      const dx = move.clientX - startX;
      const dy = move.clientY - startY;
      if (Math.hypot(dx, dy) < DEADZONE) return;
      armed = false;
      // Arm the "suppress next click" flag so the synthetic click
      // event that fires on pointerup-after-drag doesn't land in
      // `_handleCardClick` and clear the selection the user just
      // dropped. Reset in the click handler (read-once).
      this._dragOccurred = true;
      cleanup();
      this.kanbanViewLogic.startCardReorder(this.cardId, move, this);
    };

    const onUp = (up: PointerEvent): void => {
      if (up.pointerId !== pointerId) return;
      armed = false;
      cleanup();
    };

    const cleanup = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  override render() {
    const columns = this.view.columns.filter(v => !this.view.isInHeader(v.id));
    this.style.border = this.isFocus$.value
      ? '1px solid var(--streamlain-primary-color)'
      : '';

    // Live-preview reorder: stationary cards shift open/closed from a
    // frozen pointer-down snapshot. Dragged cards are represented by
    // the cursor-attached ghost and hidden in-place so the real card
    // does not also jump into the target slot.
    const shifts = this.kanbanViewLogic.reorder.liveShifts$.value;
    const offset = shifts.get(this.cardId);
    const dx = offset?.dx ?? 0;
    const dy = offset?.dy ?? 0;
    const reorderBusy = shifts.size > 0;
    const draggedIds = this.kanbanViewLogic.reorder.state$.value?.draggedIds;
    const amDragging = draggedIds?.has(this.cardId) ?? false;
    const transformStyle =
      dx !== 0 || dy !== 0 ? `translate(${dx}px, ${dy}px)` : 'none';
    const transitionStyle = reorderBusy ? 'transform 120ms ease-out' : 'none';
    this.style.setProperty('transform', transformStyle);
    this.style.setProperty('transition', transitionStyle);
    this.style.setProperty('will-change', reorderBusy ? 'transform' : '');
    this.style.setProperty('z-index', amDragging ? '100' : '');
    this.style.setProperty('opacity', amDragging ? '0' : '');

    // Flag membership in the active selection so CSS applies the grab
    // cursor and the drop-zone gate (pointerdown-drag) activates.
    const selected = this.kanbanViewLogic.rowSelection.selectedIds$.value;
    this.toggleAttribute('data-selected', selected.has(this.cardId));

    const dragHandle = this.view.readonly$.value
      ? ''
      : html`<div
          class="card-drag-handle"
          role="button"
          aria-label="Drag card"
        ></div>`;
    return html`
      ${dragHandle} ${this.renderHeader(columns)} ${this.renderBody(columns)}
      ${this.renderOps()} ${this.renderPropertyAdd()}
    `;
  }

  @property({ attribute: false })
  accessor cardId!: string;

  @property({ attribute: false })
  accessor groupKey!: string;

  @property({ attribute: false })
  accessor rowKey!: string;

  isFocus$ = signal(false);

  @property({ attribute: false })
  accessor kanbanViewLogic!: KanbanViewUILogic;

  get view() {
    return this.kanbanViewLogic.view;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-kanban-card': KanbanCard;
  }
}
